import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Loader2 } from "lucide-react";
import VoiceInput from "./VoiceInput";
import { trackEvent } from "../lib/tracker";

export default function ConsumerForm({ defaultCategory = "" }) {
    const [categories, setCategories] = useState([]);
    const [question, setQuestion] = useState("");
    const [category, setCategory] = useState(defaultCategory);
    const [zip, setZip] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/categories").then(({ data }) => {
            setCategories(data);
            if (!category && data.length) setCategory(data[0].slug);
        });
    }, []);  // eslint-disable-line

    useEffect(() => { if (defaultCategory) setCategory(defaultCategory); }, [defaultCategory]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!question.trim() || question.trim().length < 8) return setErr("Please describe your question (at least a sentence).");
        if (!/^\d{5}$/.test(zip)) return setErr("Please enter a valid 5-digit ZIP code.");
        setSubmitting(true);
        try {
            const { data } = await api.post("/consumer/ask", {
                question: question.trim(),
                category_slug: category,
                zip_code: zip,
            });
            // Funnel signal: a real consumer just asked a real question.
            trackEvent("ask_question_submit", "Initial consumer question", {
                category_slug: category, zip,
            });
            sessionStorage.setItem("ll_last_result", JSON.stringify({
                ...data, question, category_slug: category, zip,
            }));
            // Clear any prior chat thread for this (category, zip) so the user
            // starts a fresh conversation. ConsumerChat keys chats by
            // `${category_slug}-${zip}` (see ResultsPage).
            try { sessionStorage.removeItem(`ll_chat_${category}-${zip}`); } catch { /* quota */ }
            navigate("/results");
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="ll-card shadow-sm" data-testid="consumer-form">
            <div className="ll-label mb-2">Consumer Side</div>
            <h3 className="text-xl sm:text-2xl font-serif font-semibold tracking-tight mb-5">
                Start with a question, then narrow by category and ZIP code.
            </h3>

            <div className="flex items-center justify-between gap-3 mb-2">
                <label className="ll-label" htmlFor="question">Your legal question</label>
                <VoiceInput value={question} onChange={setQuestion} mode="append" />
            </div>
            <textarea
                id="question"
                rows={4}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type or speak your legal question. e.g. I was arrested and I do not know whether I should speak before I get a lawyer."
                className="ll-input mb-5 resize-none"
                data-testid="consumer-question-input"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="ll-label block mb-2" htmlFor="category">Category</label>
                    <select
                        id="category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="ll-input"
                        data-testid="consumer-category-select">
                        {categories.map((c) => (
                            <option key={c.slug} value={c.slug}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="ll-label block mb-2" htmlFor="zip">ZIP code</label>
                    <input
                        id="zip"
                        value={zip}
                        onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="30303"
                        className="ll-input"
                        inputMode="numeric"
                        data-testid="consumer-zip-input"
                    />
                </div>
            </div>

            {err && <div className="mt-4 text-sm text-destructive" data-testid="consumer-form-error">{err}</div>}

            <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                data-testid="consumer-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {submitting ? "Generating guidance..." : "Generate guidance + local lawyer results"}
            </button>

            <div className="mt-5 text-xs text-muted-foreground bg-accent/50 p-3 rounded-md border border-border">
                This product intentionally frames output as <strong>legal information</strong>, issue spotting, and next-step guidance — not attorney-client advice.
            </div>
        </form>
    );
}
